// Ports (hexagonal architecture) for the commercial core.
// The CommerceController depends only on these interfaces, so it is fully
// unit-testable with in-memory fakes. Real adapters (Supabase Auth, RevenueCat,
// Edge-Function HTTP client) implement them without the core knowing any SDK.

import type { AiOp, AiQuality, Tier } from '../domain/types';
import type {
  RemoteClear,
  RemoteStrokeEnd,
  RemoteStrokeStart,
  RemoteStrokeUpdate,
  RemoteUndo,
  StrokeEndInput,
  StrokeStartInput,
  StrokeUpdateInput,
} from '../domain/multiplayer';

export interface SessionUser {
  id: string;
  email: string | null;
  isAnonymous: boolean;
}

/** Authentication port — Supabase Auth in production. */
export interface AuthPort {
  getUser(): Promise<SessionUser | null>;
  /** Ensure *some* session exists (creates an anonymous one if needed) so the
   *  server can attribute the 3 free trial credits. Returns the user. */
  ensureSession(): Promise<SessionUser>;
  signInWithProvider(provider: 'apple' | 'google'): Promise<SessionUser>;
  signInWithEmail(email: string): Promise<void>; // magic link
  signOut(): Promise<void>;
  onAuthChange(cb: (user: SessionUser | null) => void): () => void;
}

/** Purchase/entitlement port — RevenueCat in production. */
export interface PurchasePort {
  /** Active RevenueCat entitlement ids for the current customer. */
  getActiveEntitlements(): Promise<string[]>;
  /** Link RevenueCat app_user_id to the Supabase user id. */
  identify(userId: string): Promise<void>;
  /** Purchase a tier package; resolves with the new active entitlements. */
  purchase(tier: Tier, period: 'monthly' | 'annual'): Promise<string[]>;
  restore(): Promise<string[]>;
  onEntitlementsChange(cb: (entitlementIds: string[]) => void): () => void;
}

export interface GenerateRequest {
  op: AiOp;
  imageDataUrl?: string;
  prompt?: string;
  requestId: string;
  /** Cloud provider tier; defaults to 'standard'. ('local' never hits BackendPort.) */
  quality?: AiQuality;
}

export interface GenerateResponse {
  result: unknown;
  balance: number;
  label: { aiGenerated: boolean; notice: string };
}

export interface FounderStatus {
  claimed: number;
  remaining: number;
  total: number;
  active: boolean;
}

/** Backend port — the Supabase Edge Functions over HTTPS. */
export interface BackendPort {
  /** Calls ai-generate. Throws BackendError('insufficient_credits') on 402. */
  generate(req: GenerateRequest, accessToken: string): Promise<GenerateResponse>;
  getBalance(accessToken: string): Promise<number>;
  founderStatus(): Promise<FounderStatus>;
  /**
   * Mock "Stripe" credit top-up (TEST ONLY): the server grants a fixed credit
   * pack to the (non-anonymous) caller and returns the new balance. No real
   * payment is taken — this exists so the out-of-credits flow can be exercised
   * before a real Stripe integration lands. Throws BackendError on failure
   * (e.g. 'login_required' when the caller is anonymous).
   */
  topUp(accessToken: string, requestId: string): Promise<number>;
}

/**
 * Local AI port — image generation/editing on the USER'S OWN machine
 * (e.g. SD-WebUI / ComfyUI on the M4). Free, no credits, no login, and never
 * touches the cloud Edge Function. Implementations must fail fast / report
 * unavailable so the controller can fall back to the cloud provider.
 */
export interface LocalAiPort {
  /** Cheap reachability probe; true only when a local generator is serving. */
  isAvailable(): Promise<boolean>;
  /** img2img against the local generator. Returns the monolith result shape. */
  generate(imageDataUrl: string, prompt: string): Promise<{ imageUrl: string | null }>;
}

export class BackendError extends Error {
  constructor(
    public readonly code: string,
    public readonly status: number,
    options?: { cause?: unknown },
  ) {
    super(code, options);
    this.name = 'BackendError';
  }
}

/** Provides the current access token for backend calls (from AuthPort session). */
export interface TokenProvider {
  getAccessToken(): Promise<string | null>;
}

/** Minimal id generator (mobile-Safe; production uses lib/uuid pattern). */
export interface IdProvider {
  newId(): string;
}

/**
 * Multiplayer transport port — collaborative drawing relay ("Gemeinsam zeichnen").
 * Supabase Realtime (broadcast + presence) in production; a no-op offline impl
 * when no backend is configured so the monolith never crashes.
 *
 * Lifecycle: join(roomId), then send/subscribe while active, then leave().
 * Subscribers return an unsubscribe handle. Implementations must NOT echo a
 * client's own broadcasts back to it (broadcast self=false).
 */
export interface MultiplayerPort {
  join(roomId: string): Promise<void>;
  leave(): Promise<void>;
  isActive(): boolean;
  /** Sanitised id of the joined room, or null when not in a room. */
  roomId(): string | null;
  /** Stable per-client id used as the presence key / broadcast sender id. */
  clientId(): string;

  sendStrokeStart(input: StrokeStartInput): void;
  sendStrokeUpdate(input: StrokeUpdateInput): void;
  sendStrokeEnd(input: StrokeEndInput): void;
  sendClear(): void;
  sendUndo(strokeId: string): void;

  onStrokeStart(cb: (s: RemoteStrokeStart) => void): () => void;
  onStrokeUpdate(cb: (s: RemoteStrokeUpdate) => void): () => void;
  onStrokeEnd(cb: (s: RemoteStrokeEnd) => void): () => void;
  onClear(cb: (c: RemoteClear) => void): () => void;
  onUndo(cb: (u: RemoteUndo) => void): () => void;
  /** Fires with the current participant count whenever presence changes. */
  onParticipants(cb: (count: number) => void): () => void;
}
